import { isAnyBuyAndSellCategory, isAnyCarsVehiclesCategory } from '@kijiji/category'
import { ResultsView } from '@kijiji/generated/graphql-types'

import { createSrpGptConfig } from '@/components-page/srp/advertisement/createSrpGptConfig'
import { useSrpGpt } from '@/components-page/srp/advertisement/useSrpGpt'
import { extractCategoryIdsFromPath } from '@/domain/category/extractCategoryIdsFromPath'
import { useAdSense } from '@/features/advertisement/hooks/useAdSense'
import { cleanCategoryPathForDisplayAds } from '@/features/advertisement/utils/cleanCategoryPathForDisplayAds'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useLocale } from '@/hooks/useLocale'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { getUserDevice } from '@/utils/getUserDevice'

/**
 * Custom hook to handle the logic for SRP advertisements
 * returns configurations and targeting values to be passed to AdvertisingProvider
 *
 * @returns gpt - targeting values
 * @returns srpGptConfig - GPT configuration object
 */
export const useSrpAds = (userAgent?: string) => {
  const isMobile = getUserDevice(userAgent).isPhoneOrTablet

  const { data } = useGetSearchResultsData()
  const { currentPage, viewType } = useGetSrpState()
  const { routeLocale } = useLocale()
  const { location } = useGetLocation()
  const { category } = useGetSearchCategory()

  const { searchQuery, pagination, controls } = data || {}

  const cleanedPath = cleanCategoryPathForDisplayAds(category.path || [])
  const allCategoryIds = extractCategoryIdsFromPath(category.path || [])

  const isMastheadReduced = useToggle(RemoteParamKeys.SMALLER_MASTHEAD_TOGGLE)?.enabled ?? false
  const removeAdsenseMwebToggle = useToggle(RemoteParamKeys.REMOVE_ADSENSE_MWEB)

  // Toggle off AdSense Top placement on mWeb for Autos and Buy and Sell
  const isRemoveAdsenseMwebToggleEnabled = !!(removeAdsenseMwebToggle?.enabled && isMobile)
  const shouldHideAdsenseTop =
    isRemoveAdsenseMwebToggleEnabled &&
    (isAnyBuyAndSellCategory(category.id) || isAnyCarsVehiclesCategory(category.id))

  const gpt = useSrpGpt({
    categoryIds: allCategoryIds,
    currentPage: currentPage,
    keywords: searchQuery?.keywords ?? '',
    locationId: location.id,
    routeLocale,
    filters: controls?.filtering ?? [],
    categories: cleanedPath,
    totalListings: pagination?.totalCount ?? 0,
    viewType,
  })

  const srpGptConfig = createSrpGptConfig({
    gptTargeting: gpt,
    isMastheadReduced: !!isMastheadReduced,
    isDesktopMapView: viewType === ResultsView.Map && !isMobile,
  })

  const configParams = {
    allCategoryIds: allCategoryIds,
    locationId: location.id,
    englishLocationName: location?.name?.en_CA ?? '',
    currentPage,
    keywordsOrTitle: searchQuery?.keywords ?? '',
    routeLocale,
    categoryPath: category.path ?? [],
  }

  useAdSense({
    configParams,
    active: true,
    containers: [
      ...(shouldHideAdsenseTop ? [] : [{ container: 'AFSTop', width: '100%', number: 2 }]),
      { container: 'AFSBottom', width: '100%', number: 2 },
    ],
  })

  return {
    srpGptConfig,
    gpt,
  }
}
